import React, {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import {LocalLink} from 'i18n/LocalLink'
import styled from 'styled-components'
import {Helmet} from 'react-helmet'
import Page from 'attendee/theme/Page'
import {
  DEFAULT_OG_IMAGE,
  SITE_ORIGIN,
  truncateMetaDescription,
} from 'seo/siteMeta'
import {FullPageLoader} from 'attendee/theme/FullPageLoader'
import {routes} from 'routes/routes'
import {useI18n} from 'i18n/I18nContext'
import {withLocalePath} from 'i18n/localePaths'
import {appLocaleToBcp47} from 'i18n/locales'
import {blogApiClient, BlogPost as BlogPostType} from 'blog/apiClient'
import {publicImageUrl} from 'organization/ImageSelector'
import defaultEventImage from 'images/defaultEventImage.webp'

export function BlogPost() {
  const {t, locale} = useI18n()
  const {blogSlug} = useParams<{blogSlug: string}>()
  const [post, setPost] = useState<BlogPostType | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    if (!blogSlug) return
    setLoading(true)
    setPost(null)
    blogApiClient
      .getPost(blogSlug)
      .then(setPost)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
  }, [blogSlug])

  if (loading) return <FullPageLoader />

  if (notFound || !post) {
    return (
      <Page>
        <section className="dark_light_class pt-80 pb-80">
          <div className="container" style={{textAlign: 'center'}}>
            <h2>{t('blog.notFoundTitle')}</h2>
            <p style={{color: '#888', marginBottom: 24}}>
              {t('blog.notFoundBody')}
            </p>
            <LocalLink to={routes.blog.root} className="input__btn">
              {t('blog.backToBlog')}
            </LocalLink>
          </div>
        </section>
      </Page>
    )
  }

  const formattedDate = post.published_at
    ? new Date(post.published_at).toLocaleDateString(appLocaleToBcp47(locale), {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null

  const heroImage = post.featured_image
    ? publicImageUrl(post.featured_image)
    : null
  const title = post.meta_title || post.title
  const rawDescription = post.meta_description || post.excerpt || ''
  const description = rawDescription
    ? truncateMetaDescription(rawDescription)
    : truncateMetaDescription(t('blog.metaFallback').replace('{title}', title))
  const pageUrl = `${SITE_ORIGIN}${withLocalePath(locale, `${routes.blog.root}/${post.slug}`)}`
  const ogImage = heroImage || DEFAULT_OG_IMAGE

  const articleLdJson = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${pageUrl}#article`,
    headline: title,
    description,
    image: ogImage,
    inLanguage: appLocaleToBcp47(locale),
    mainEntityOfPage: {'@type': 'WebPage', '@id': pageUrl},
    isPartOf: {'@id': `${SITE_ORIGIN}/#website`},
    publisher: {'@id': `${SITE_ORIGIN}/#organization`},
    datePublished: post.published_at || undefined,
    dateModified: post.published_at || undefined,
    author: post.author
      ? {'@type': 'Person', name: post.author.name}
      : {'@id': `${SITE_ORIGIN}/#organization`},
    articleSection: post.category?.name,
    keywords: (post.tags ?? []).map((tg) => tg.name).join(', ') || undefined,
  })

  return (
    <Page>
      <>
        <Helmet>
          <title>{`${title} | ${t('blog.postTitleSuffix')}`}</title>
          <meta name="description" content={description} />
          <link rel="canonical" href={pageUrl} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta property="og:url" content={pageUrl} />
          <meta property="og:image" content={ogImage} />
          <meta property="og:image:secure_url" content={ogImage} />
          <meta property="og:image:type" content="image/jpeg" />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          {post.published_at && (
            <meta
              property="article:published_time"
              content={post.published_at}
            />
          )}
          {post.published_at && (
            <meta
              property="article:modified_time"
              content={post.published_at}
            />
          )}
          {post.author && (
            <meta property="article:author" content={post.author.name} />
          )}
          {post.category && (
            <meta property="article:section" content={post.category.name} />
          )}
          {(post.tags ?? []).map((tg) => (
            <meta
              key={`atag-${tg.slug}`}
              property="article:tag"
              content={tg.name}
            />
          ))}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={description} />
          <meta name="twitter:image" content={ogImage} />
          <script type="application/ld+json">{articleLdJson}</script>
        </Helmet>

        {/* Hero image */}
        {heroImage && <HeroImage $src={heroImage} />}

        <section className="contact_section dark_light_class pt-55 pb-80">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-xxl-8 col-xl-9 col-lg-10">
                {/* Breadcrumb */}
                <Breadcrumb>
                  <LocalLink to={routes.blog.root}>
                    {t('blog.breadcrumbName')}
                  </LocalLink>
                  {post.category && (
                    <>
                      <span> / </span>
                      <LocalLink
                        to={`${routes.blog.root}?category=${post.category.slug}`}
                      >
                        {post.category.name}
                      </LocalLink>
                    </>
                  )}
                  <span> / </span>
                  <span>{post.title}</span>
                </Breadcrumb>

                {/* Category badge */}
                {post.category && (
                  <div
                    className="event__tag-item mb-20"
                    style={{display: 'inline-block'}}
                  >
                    <span
                      style={{background: post.category.color, fontSize: 12}}
                    >
                      {post.category.name}
                    </span>
                  </div>
                )}

                {/* Title */}
                <div className="section__title-wrapper is-left mb-30">
                  <h1
                    className="section__title"
                    style={{fontSize: 'clamp(1.5rem, 3vw, 2rem)'}}
                  >
                    {post.title}
                  </h1>
                </div>

                {/* Meta */}
                <MetaRow className="news__meta-status mb-30">
                  {post.author && (
                    <span>
                      <i className="flaticon-user mr-5"></i>
                      {post.author.name}
                    </span>
                  )}
                  {formattedDate && (
                    <span>
                      <i className="flaticon-calendar-1 mr-5"></i>
                      {formattedDate}
                    </span>
                  )}
                  <span style={{color: '#f7426f', fontWeight: 600}}>
                    ⏱{' '}
                    {t('blog.readingTimeLong').replace(
                      '{n}',
                      String(post.reading_time),
                    )}
                  </span>
                  {post.views > 0 && (
                    <span style={{color: '#888'}}>
                      👁{' '}
                      {t('blog.viewsCount').replace(
                        '{n}',
                        post.views.toLocaleString(appLocaleToBcp47(locale)),
                      )}
                    </span>
                  )}
                </MetaRow>

                {/* Excerpt */}
                {post.excerpt && <Excerpt>{post.excerpt}</Excerpt>}

                {/* Rich content */}
                <RichContent
                  dangerouslySetInnerHTML={{__html: post.content ?? ''}}
                />

                {/* Tags */}
                {post.tags.length > 0 && (
                  <TagsRow>
                    {post.tags.map((tag) => (
                      <LocalLink
                        key={tag.slug}
                        to={`${routes.blog.root}?tag=${tag.slug}`}
                        className="event__tag-item"
                        style={{textDecoration: 'none'}}
                      >
                        <span style={{fontSize: 12}}>#{tag.name}</span>
                      </LocalLink>
                    ))}
                  </TagsRow>
                )}

                {/* Back */}
                <div className="mt-40">
                  <LocalLink to={routes.blog.root} className="input__btn">
                    {t('blog.backToBlog')}
                  </LocalLink>
                </div>
              </div>
            </div>

            {/* Related posts */}
            {post.related && post.related.length > 0 && (
              <RelatedSection>
                <div className="section__title-wrapper is-left mb-30">
                  <span className="section__subtitle">
                    {t('blog.relatedSubtitle')}
                  </span>
                  <h2 className="section__title" style={{fontSize: '1.6rem'}}>
                    {t('blog.relatedTitle')}
                  </h2>
                </div>
                <div className="row">
                  {post.related.map((rel) => {
                    const relImage = rel.featured_image
                      ? publicImageUrl(rel.featured_image)
                      : (defaultEventImage as string)
                    const relDate = rel.published_at
                      ? new Date(rel.published_at).toLocaleDateString(
                          appLocaleToBcp47(locale),
                          {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          },
                        )
                      : null
                    return (
                      <div key={rel.id} className="col-lg-4 col-md-6 col-xs-12">
                        <RelatedCard>
                          <LocalLink to={`${routes.blog.root}/${rel.slug}`}>
                            <RelatedImage $src={relImage as string} />
                          </LocalLink>
                          <RelatedBody>
                            {rel.category && (
                              <span
                                style={{
                                  display: 'inline-block',
                                  fontSize: 11,
                                  padding: '2px 10px',
                                  borderRadius: 20,
                                  background: rel.category.color,
                                  color: '#fff',
                                  marginBottom: 8,
                                }}
                              >
                                {rel.category.name}
                              </span>
                            )}
                            <RelatedTitle>
                              <LocalLink to={`${routes.blog.root}/${rel.slug}`}>
                                {rel.title}
                              </LocalLink>
                            </RelatedTitle>
                            <RelatedMeta>
                              {relDate && <span>{relDate}</span>}
                              <span style={{color: '#f7426f'}}>
                                ⏱{' '}
                                {t('blog.minReadShort').replace(
                                  '{n}',
                                  String(rel.reading_time),
                                )}
                              </span>
                            </RelatedMeta>
                          </RelatedBody>
                        </RelatedCard>
                      </div>
                    )
                  })}
                </div>
              </RelatedSection>
            )}
          </div>
        </section>
      </>
    </Page>
  )
}

/* ─── Styled ─────────────────────────────────────────────────────────────── */

const HeroImage = styled.div<{$src: string}>`
  width: 100%;
  height: 380px;
  background-image: url(${(p) => p.$src});
  background-size: cover;
  background-position: center;

  @media (max-width: 768px) {
    height: 220px;
  }
`

const Breadcrumb = styled.nav`
  font-size: 0.82rem;
  color: #aaa;
  margin-bottom: 16px;
  & a {
    color: #888;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
  & span {
    margin: 0 4px;
    &:last-child {
      color: #555;
    }
  }
`

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 24px !important;
  align-items: center;
`

const Excerpt = styled.p`
  font-size: 1.05rem;
  font-style: italic;
  color: #555;
  line-height: 1.7;
  margin-bottom: 28px;
  padding-left: 16px;
  border-left: 3px solid #f7426f;
`

const RichContent = styled.div`
  line-height: 1.8;
  font-size: 0.97rem;

  h1 {
    font-size: 1.8rem;
    font-weight: 800;
    margin: 1.5em 0 0.5em;
  }
  h2 {
    font-size: 1.4rem;
    font-weight: 700;
    margin: 1.5em 0 0.5em;
  }
  h3 {
    font-size: 1.2rem;
    font-weight: 600;
    margin: 1.2em 0 0.4em;
  }
  h4 {
    font-size: 1.05rem;
    font-weight: 600;
    margin: 1em 0 0.4em;
  }

  p {
    margin-bottom: 1.2em;
  }
  ul,
  ol {
    margin: 0.8em 0 1.2em 1.5em;
  }
  ul {
    list-style: disc;
  }
  ol {
    list-style: decimal;
  }
  li {
    margin: 0.4em 0;
  }

  a {
    color: #f7426f;
    text-decoration: underline;
  }

  blockquote {
    border-left: 4px solid #f7426f;
    background: #fff5f7;
    padding: 12px 18px;
    margin: 1.2em 0;
    border-radius: 0 6px 6px 0;
    & p {
      margin: 0;
    }
  }

  pre,
  code {
    font-family: 'Fira Mono', 'Consolas', monospace;
    font-size: 0.88rem;
    background: #f5f5f5;
    border-radius: 4px;
  }
  pre {
    padding: 14px 18px;
    overflow-x: auto;
    margin: 1em 0;
  }
  code {
    padding: 2px 6px;
  }

  img {
    max-width: 100%;
    border-radius: 6px;
    margin: 1em 0;
  }
  strong {
    font-weight: 700;
  }
  em {
    font-style: italic;
  }
`

const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin: 28px 0 8px;
`

const RelatedSection = styled.div`
  margin-top: 60px;
  padding-top: 40px;
  border-top: 1px solid #f0f0f0;
`

const RelatedCard = styled.div`
  background: #f9f9f9;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 30px;
  border-bottom: 2px solid #f7426f;
  transition:
    transform 0.15s,
    box-shadow 0.15s;
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  }
`

const RelatedImage = styled.div<{$src: string}>`
  width: 100%;
  height: 160px;
  background-image: url(${(p) => p.$src});
  background-size: cover;
  background-position: center;
  display: block;
`

const RelatedBody = styled.div`
  padding: 14px 16px 16px;
`

const RelatedTitle = styled.h4`
  font-size: 0.92rem;
  font-weight: 700;
  margin: 0 0 8px;
  line-height: 1.4;
  & a {
    color: inherit;
    text-decoration: none;
    &:hover {
      color: #f7426f;
    }
  }
`

const RelatedMeta = styled.div`
  display: flex;
  gap: 12px;
  font-size: 0.78rem;
  color: #888;
`
